import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { trackEvent, trackEventOnce } from '../utils/analytics';

const CHECKLIST_EVENT_NAME = 'school_maintenance_checklist';
const DEFAULT_CHECKLIST_URL = '/school-maintenance-checklist.html';

export const SchoolChecklistLeadMagnet = ({ pageSlug, pageTitle }) => {
  const [email, setEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [checklistUrl, setChecklistUrl] = useState(DEFAULT_CHECKLIST_URL);
  const [leadId, setLeadId] = useState('');

  useEffect(() => {
    trackEventOnce(`lead_magnet_view_${pageSlug}`, 'lead_magnet_view', {
      lead_magnet: CHECKLIST_EVENT_NAME,
      page_slug: pageSlug
    });
  }, [pageSlug]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Enter a school email to open the checklist.');
      return;
    }

    setStatus('submitting');
    trackEvent('lead_magnet_submit_attempt', {
      lead_magnet: CHECKLIST_EVENT_NAME,
      page_slug: pageSlug
    });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/lead-magnet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          schoolName: schoolName.trim() || undefined,
          role: role.trim() || undefined,
          pageSlug,
          source: `${pageSlug}_b2b_page`,
          leadMagnet: 'school-maintenance-checklist',
          consent: true
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Unable to prepare the checklist.');

      setChecklistUrl(payload.checklist_url || DEFAULT_CHECKLIST_URL);
      setLeadId(payload.lead_id || '');
      setStatus('submitted');
      trackEvent('lead_magnet_submit_success', {
        lead_magnet: CHECKLIST_EVENT_NAME,
        page_slug: pageSlug
      });
    } catch (submitError) {
      setError(submitError.message || 'Unable to prepare the checklist. Please try again.');
      setStatus('idle');
      trackEvent('lead_magnet_submit_error', {
        lead_magnet: CHECKLIST_EVENT_NAME,
        page_slug: pageSlug,
        error_type: 'api_or_validation_error'
      });
    }
  };

  const handleDownload = () => {
    trackEvent('lead_magnet_download', {
      lead_magnet: CHECKLIST_EVENT_NAME,
      page_slug: pageSlug
    });
  };

  return (
    <section className="py-14 bg-white" data-testid={`lead-magnet-section-${pageSlug}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-8 border border-gray-200 bg-gray-50 rounded-sm overflow-hidden" data-testid={`lead-magnet-panel-${pageSlug}`}>
          <div className="lg:col-span-5 bg-gray-900 text-white p-6 sm:p-8">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-gray-400 mb-3" data-testid={`lead-magnet-eyebrow-${pageSlug}`}>School maintenance checklist</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-4" data-testid={`lead-magnet-heading-${pageSlug}`}>Download the school 3D printer maintenance checklist</h2>
            <p className="text-sm text-gray-300 leading-relaxed mb-6" data-testid={`lead-magnet-summary-${pageSlug}`}>
              A practical pre-term checklist for STEM coordinators and IT teams to reduce clogs, calibration drift and preventable printer downtime.
            </p>
            <div className="grid grid-cols-2 gap-px bg-gray-700 border border-gray-700" data-testid={`lead-magnet-benefits-grid-${pageSlug}`}>
              {['Hotend checks', 'Bed checks', 'Motion checks', 'Fleet notes'].map((item) => (
                <div key={item} className="bg-gray-900 px-4 py-3" data-testid={`lead-magnet-benefit-${pageSlug}-${item.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="text-sm font-extrabold" data-testid={`lead-magnet-benefit-text-${pageSlug}-${item.toLowerCase().replace(/\s+/g, '-')}`}>{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 p-6 sm:p-8 bg-white" data-testid={`lead-magnet-form-wrap-${pageSlug}`}>
            {status === 'submitted' ? (
              <div className="h-full flex flex-col justify-center" data-testid={`lead-magnet-success-${pageSlug}`}>
                <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-gray-500 mb-3" data-testid={`lead-magnet-success-eyebrow-${pageSlug}`}>Checklist ready</p>
                <h3 className="text-xl font-extrabold tracking-tight mb-3" data-testid={`lead-magnet-success-heading-${pageSlug}`}>Open the maintenance checklist now</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-5" data-testid={`lead-magnet-success-message-${pageSlug}`}>
                  Use it before term projects, lab audits or purchase-order maintenance planning.
                </p>
                {leadId && <p className="text-xs text-gray-500 mb-4" data-testid={`lead-magnet-lead-id-${pageSlug}`}>Reference: {leadId}</p>}
                <a
                  href={checklistUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={handleDownload}
                  className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white text-sm font-extrabold px-5 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors w-full sm:w-auto"
                  data-testid={`lead-magnet-download-link-${pageSlug}`}
                >
                  <Download className="h-4 w-4" />
                  Open checklist
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid={`lead-magnet-form-${pageSlug}`}>
                <div>
                  <h3 className="text-xl font-extrabold tracking-tight mb-2" data-testid={`lead-magnet-form-heading-${pageSlug}`}>Send the checklist to your screen</h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-testid={`lead-magnet-form-summary-${pageSlug}`}>Built for schools comparing repair, maintenance and fleet servicing options from {pageTitle}.</p>
                </div>

                {error && <div className="border border-red-200 bg-red-50 text-red-700 text-xs font-semibold px-3 py-2 rounded-sm" data-testid={`lead-magnet-error-${pageSlug}`}>{error}</div>}

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label htmlFor={`lead-magnet-email-${pageSlug}`} className="text-xs font-semibold text-gray-700">School email *</Label>
                    <Input
                      id={`lead-magnet-email-${pageSlug}`}
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="name@school.edu.au"
                      required
                      className="h-11 rounded-sm"
                      data-testid={`lead-magnet-email-input-${pageSlug}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`lead-magnet-school-${pageSlug}`} className="text-xs font-semibold text-gray-700">School / organisation</Label>
                    <Input
                      id={`lead-magnet-school-${pageSlug}`}
                      value={schoolName}
                      onChange={(event) => setSchoolName(event.target.value)}
                      placeholder="School name"
                      className="h-11 rounded-sm"
                      data-testid={`lead-magnet-school-input-${pageSlug}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`lead-magnet-role-${pageSlug}`} className="text-xs font-semibold text-gray-700">Role</Label>
                    <Input
                      id={`lead-magnet-role-${pageSlug}`}
                      value={role}
                      onChange={(event) => setRole(event.target.value)}
                      placeholder="STEM coordinator"
                      className="h-11 rounded-sm"
                      data-testid={`lead-magnet-role-input-${pageSlug}`}
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500 leading-relaxed" data-testid={`lead-magnet-privacy-note-${pageSlug}`}>We use this to help schools plan maintenance and repair support. The checklist opens immediately after submission.</p>
                <Button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="bg-gray-900 hover:bg-black text-white text-sm font-extrabold px-5 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors w-full sm:w-auto disabled:opacity-70"
                  data-testid={`lead-magnet-submit-button-${pageSlug}`}
                >
                  {status === 'submitting' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Preparing checklist</> : 'Get checklist'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};